// =============================================================================
// Is the signed-in user's time zone actually set?
//
// WHY THIS EXISTS
// Every note is stored and timestamped in UTC, so the evidence is correct no
// matter what this returns. What depends on the profile time zone is how those
// UTC instants are RENDERED, and the rendering falls back to UTC on the server
// when the profile value is missing. A user in Pacific time with no time zone
// set therefore sees a note card reading "Sep 04, 2026 - 7:05 PM" at 12:07 PM
// local: seven hours out, and plausible enough to be believed.
//
// WHAT "NOT SET" LOOKS LIKE
// Admin.[User].TimeZone is NVARCHAR(200) NULL with no default, so the stored
// value is genuinely NULL until the user picks one. By the time it reaches this
// file it can be any of:
//
//   1. null            - the API leaves UsersProfile.IanaTimeZoneId unassigned
//                        when TimeZone is empty, and it serializes as JSON null
//   2. undefined       - the field is absent (profile not loaded yet, a cached
//                        profile envelope written before the field existed, or a
//                        null-stripping serializer)
//   3. ''              - UserModel's own initial value, and what the medium
//                        profile returns for TimeZone (`as string ?? ""`)
//   4. whitespace only - trimmed to nothing
//   5. 'null'/'undefined' as literal text - a value that went through string
//                        interpolation somewhere on the way here (the SignalR
//                        TimeZoneUpdated handler assigns the pushed value
//                        straight onto the user object)
//   6. a non-empty id the browser cannot resolve - renders "Invalid DateTime"
//                        rather than a wrong time, but is still not usable
//
// A check that catches only case 3 reports all-clear for the other five, which
// is worse than no check at all. `isTimeZoneValueBlank` covers 1 through 5 and
// `isResolvableTimeZone` covers 6.
//
// 'UTC' IS NOT TREATED AS UNSET. A user in Reykjavik or on a UTC-standardised
// team can legitimately choose it, and calling that "not set" would be a false
// alarm. The "UTC" sentinel the API substitutes for a missing value lives only
// in the server's own core profile and never reaches the client, so there is no
// way to confuse the two here.
// =============================================================================

export type TimeZoneStatus = 'set' | 'unset' | 'unresolvable';

/** The two fields the profile carries. Structural so tests and the UserModel
 *  both satisfy it without importing each other. */
export interface TimeZoneCarrier {
  /** Windows id, e.g. "Pacific Standard Time". The column that is stored. */
  timeZone?: string | null;
  /** IANA id, e.g. "America/Vancouver". Derived from `timeZone` by the API and
   *  the one every client-side formatter takes. */
  ianaTimeZoneId?: string | null;
}

// Values that mean "nothing was chosen" once the string is normalised.
const BLANK_VALUES = ['', 'null', 'undefined'];

/** TRUE when the value carries no time zone, in any of its representations. */
export function isTimeZoneValueBlank(value: unknown): boolean {
  if (typeof value !== 'string') {
    // Covers null and undefined, and anything non-string that a loosely typed
    // API response could put here.
    return true;
  }
  return BLANK_VALUES.indexOf(value.trim().toLowerCase()) > -1;
}

/** TRUE when the browser can actually resolve the id to a zone. This is the
 *  same validity test Luxon applies before it produces "Invalid DateTime". */
export function isResolvableTimeZone(id: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: id }).format(0);
    return true;
  } catch {
    return false;
  }
}

/**
 * Classify the profile's time zone.
 *
 * Blank in EITHER field counts as unset. The two are meant to move together
 * (the API derives the IANA id from the Windows id), so a half-populated
 * profile means at least one rendering path is already falling back. Erring
 * toward "unset" can only ever produce an extra warning; erring the other way
 * produces a silent, wrong clock time.
 */
export function getTimeZoneStatus(user: TimeZoneCarrier | null | undefined): TimeZoneStatus {
  if (!user) {
    return 'unset';
  }
  if (isTimeZoneValueBlank(user.ianaTimeZoneId) || isTimeZoneValueBlank(user.timeZone)) {
    return 'unset';
  }
  if (!isResolvableTimeZone((user.ianaTimeZoneId as string).trim())) {
    return 'unresolvable';
  }
  return 'set';
}

/** Convenience for callers that only need the yes/no. */
export function isTimeZoneMissing(user: TimeZoneCarrier | null | undefined): boolean {
  return getTimeZoneStatus(user) !== 'set';
}
